// src/api/ownerPortalApi.js
import api from "./axios";

// Optional: keep using same key name as rest of app
const TOKEN_KEY = "token";
const OWNER_TOKEN_KEY = "ownerToken";

/* =============================================================================
   Token helpers
============================================================================= */

export function getOwnerToken() {
  return (
    localStorage.getItem(OWNER_TOKEN_KEY) ||
    localStorage.getItem(TOKEN_KEY) ||
    ""
  );
}

export function setOwnerToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(OWNER_TOKEN_KEY, token);
    // optional: keep legacy key in sync if other modules read it
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
}

export function clearOwnerToken() {
  try {
    localStorage.removeItem(OWNER_TOKEN_KEY);
    // do not remove TOKEN_KEY globally unless you're sure other roles don't use it
    // localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

/* =============================================================================
   Helpers
============================================================================= */

function withAuth(config = {}) {
  const token = getOwnerToken();
  const headers = { ...(config.headers || {}) };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  return { ...config, headers };
}

function unwrapAxiosError(e, fallback = "Request failed. Please try again.") {
  // Network / CORS / timeout
  if (!e?.response) {
    const msg =
      e?.message?.includes("Network Error")
        ? "Network error. Please check your internet connection."
        : e?.message || "Server unreachable. Please try again.";
    const err = new Error(msg);
    err.status = 0;
    err.data = null;
    throw err;
  }

  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    (typeof data === "string" && data) ||
    data?.message ||
    data?.error ||
    data?.detail ||
    e?.message ||
    fallback;

  const err = new Error(msg);
  err.status = status;
  err.data = data;
  throw err;
}


function normalizeLocationSuggestion(item) {
  if (!item) return null;
  if (typeof item === "string") {
    const text = item.trim();
    return text ? { placeId: "", primaryText: text, secondaryText: "", fullText: text, latitude: null, longitude: null, source: "" } : null;
  }
  const placeId = item.placeId ?? item.place_id ?? "";
  const primaryText = item.primaryText ?? item.mainText ?? item.structured_formatting?.main_text ?? item.displayName ?? item.name ?? "";
  const secondaryText = item.secondaryText ?? item.secondary_text ?? item.structured_formatting?.secondary_text ?? item.formattedAddress ?? "";
  const fullText = item.fullText ?? item.description ?? [primaryText, secondaryText].filter(Boolean).join(", ");
  const latitude = item.latitude == null ? null : Number(item.latitude);
  const longitude = item.longitude == null ? null : Number(item.longitude);
  const source = item.source ?? "";
  const text = String(fullText || "").trim();
  if (!text) return null;
  return {
    placeId: String(placeId || "").trim(),
    primaryText: String(primaryText || text).trim(),
    secondaryText: String(secondaryText || "").trim(),
    fullText: text,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    source: String(source || "").trim(),
  };
}

/* =============================================================================
   OWNER AUTH (legacy)
============================================================================= */

/**
 * Legacy: email/password owner sign-in
 * POST /owners/signin -> { token, email, ... }
 */
export async function ownerSignInApi({ email, password }) {
  try {
    const { data } = await api.post("/owners/signin", { email, password });
    if (data?.token) setOwnerToken(data.token);
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Owner sign-in failed.");
  }
}

/**
 * Driver sign in (optional, for future driver portal)
 * POST /drivers/signin -> { token, phoneNumber?, ... }
 */
export async function driverSignInApi({ phoneNumber, password }) {
  try {
    const { data } = await api.post("/drivers/signin", {
      phoneNumber,
      password,
    });
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Driver sign-in failed.");
  }
}

/**
 * Legacy current owner info
 * GET /owners/current-owner -> { email, ... }
 */
export async function currentOwnerApi() {
  try {
    const { data } = await api.get("/owners/current-owner", withAuth());
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to load current owner.");
  }
}

/**
 * Owner sign-out
 * POST /owners/signout
 */
export async function ownerSignOutApi() {
  try {
    await api.post("/owners/signout", {}, withAuth());
  } catch (_) {
    // ignore
  } finally {
    clearOwnerToken();
  }
}

/* ─────────────────────────────────────────────────────────────
  Phone-first owner auth (OTP + password)
  NOTE: Do NOT send Authorization header for these public endpoints.
───────────────────────────────────────────────────────────── */

/**
 * Request OTP
 * POST /owners/auth/otp/request
 * body: { phoneNumber }
 */
export async function ownerRequestOtpApi({ phoneNumber }) {
  try {
    const { data } = await api.post("/owners/auth/otp/request", { phoneNumber });
    return data; // { message, phoneNumber, devOtp? }
  } catch (e) {
    unwrapAxiosError(e, "OTP request failed.");
  }
}

/**
 * Verify OTP (signin / signup completion)
 * POST /owners/auth/otp/verify
 * body: { phoneNumber, otp, name?, address?, email? }
 */
export async function ownerVerifyOtpApi(payload) {
  try {
    const { data } = await api.post("/owners/auth/otp/verify", payload || {});
    if (data?.token) setOwnerToken(data.token);
    return data; // { token?, isNewOwner?, phoneNumber, role?, hasPassword? }
  } catch (e) {
    unwrapAxiosError(e, "OTP verification failed.");
  }
}

/**
 * Password sign-in
 * POST /owners/auth/password/signin
 * body: { phoneNumber, password }
 */
export async function ownerPasswordSignInApi({ phoneNumber, password }) {
  try {
    const { data } = await api.post("/owners/auth/password/signin", {
      phoneNumber,
      password,
    });
    if (data?.token) setOwnerToken(data.token);
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Password sign-in failed.");
  }
}

/**
 * Set / change owner password (JWT protected)
 * POST /owners/auth/password/set
 * body: { newPassword }
 */
export async function ownerSetPasswordApi(newPassword) {
  try {
    const { data } = await api.post(
      "/owners/auth/password/set",
      { newPassword },
      withAuth()
    );
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to set password.");
  }
}

/**
 * Complete owner profile (JWT protected)
 * POST /owners/auth/profile/complete
 * body: { name, address, email? }
 */
export async function ownerCompleteProfileApi(payload) {
  try {
    const { data } = await api.post(
      "/owners/auth/profile/complete",
      payload || {},
      withAuth()
    );
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to complete profile.");
  }
}

/**
 * Owner profile (phone-first)
 * GET /owners/profile
 */
export async function fetchOwnerProfile() {
  try {
    const { data } = await api.get("/owners/profile", withAuth());
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to load owner profile.");
  }
}

/* ─────────────────────────────────────────────────────────────
  Legacy owner creation + address autocomplete
───────────────────────────────────────────────────────────── */

/**
 * Legacy owner sign-up
 * POST /owners
 */
export async function ownerSignUpApi(payload) {
  try {
    const { data } = await api.post("/owners", payload || {}, withAuth());
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Owner signup failed.");
  }
}

/**
 * Owner address autocomplete
 * GET /owners/autocomplete/location/:query
 */
export async function ownerLocationAutocompleteApi(query, opts = {}) {
  try {
    const trimmed = String(query || "").trim();
    if (!trimmed) return [];

    const config = withAuth({ params: {} });
    if (opts.lat != null && Number.isFinite(Number(opts.lat))) config.params.lat = Number(opts.lat);
    if (opts.lng != null && Number.isFinite(Number(opts.lng))) config.params.lng = Number(opts.lng);
    if (opts.sessionToken) config.params.sessionToken = String(opts.sessionToken).trim();
    if (opts.signal) config.signal = opts.signal;

    const { data } = await api.get(
      `/api/vehicles/autocomplete/location-predictions/${encodeURIComponent(trimmed)}`,
      config
    );

    const raw = Array.isArray(data) ? data : Array.isArray(data?.predictions) ? data.predictions : [];
    return raw.map(normalizeLocationSuggestion).filter(Boolean);
  } catch (e) {
    unwrapAxiosError(e, "Location autocomplete failed.");
  }
}

/* =============================================================================
   OWNER PAYOUT ACCOUNT
============================================================================= */

export async function fetchOwnerPayoutAccount() {
  try {
    const { data } = await api.get("/owners/payout/account", withAuth());
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to load payout account.");
  }
}

export async function saveOwnerPayoutAccount(payload) {
  try {
    const { data } = await api.put(
      "/owners/payout/account",
      payload || {},
      withAuth()
    );
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to save payout account draft.");
  }
}

export async function submitOwnerPayoutAccount(payload) {
  try {
    const { data } = await api.post(
      "/owners/payout/account/submit",
      payload || {},
      withAuth()
    );
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to submit payout account.");
  }
}

export async function fetchOwnerPayoutSummary() {
  try {
    const { data } = await api.get("/owners/payout/summary", withAuth());
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to load payout summary.");
  }
}

export async function fetchOwnerPayoutTransactions(params = {}) {
  try {
    const page = Number.isFinite(Number(params.page)) ? Number(params.page) : 0;
    const size = Number.isFinite(Number(params.size)) ? Number(params.size) : 20;

    const { data } = await api.get("/owners/payout/transactions", {
      ...withAuth(),
      params: { page, size },
    });

    return data;
  } catch (e) {
    unwrapAxiosError(e, "Failed to load payout transactions.");
  }
}

export async function ownerTestSignInApi({ phoneNumber, secret }) {
  try {
    const { data } = await api.post("/owners/auth/test/signin", {
      phoneNumber,
      secret,
    });
    if (data?.token) setOwnerToken(data.token);
    return data;
  } catch (e) {
    unwrapAxiosError(e, "Test sign-in failed.");
  }
}